import React from 'react';
import { List } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import {
    Input, onFieldChange, toasty, onReactSelectChanged, FormCheckbox,
} from '../../common/forms/FormElements';
import { SaveButton } from '../../common/forms/SaveButton';
import { BlockBorder } from '../../common/Block';
import { TipAlert } from '../../common/TipAlert';
import ValidatedSelect from '../../common/forms/ValidatedSelect';

/**
 * Shared class component for changing a timesheet's job date and/or start time.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */

class ChangeJobDateViewModel {
    timesheetId = '';

    selectedDate = '';

    newStartTime = '';
}

const formatDateYYYYMMDD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

class ChangeJobDateForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            data: new ChangeJobDateViewModel(),
            timesheetOptions: [],
            canPickDate: false,
            checkingInvoice: false,
            lastInvoicedDate: null,
            minSelectableDate: '',
            changeStartTime: false,
        };
        this.onChange = onFieldChange.bind(this);
        this.onReactSelectChanged = onReactSelectChanged.bind(this);
        this.onChangeStartTimeToggle = this.onChangeStartTimeToggle.bind(this);
    }

    onChangeStartTimeToggle({ target: { checked } }) {
        this.setState((prev) => ({
            changeStartTime: checked,
            data: { ...prev.data, newStartTime: checked ? prev.data.newStartTime : '' },
        }));
    }

    async checkLastInvoiced(timesheetId) {
        if (!timesheetId) {
            this.setState({
                lastInvoicedDate: null,
                minSelectableDate: '',
                canPickDate: false,
                checkingInvoice: false,
            });
            return;
        }

        this.setState({ checkingInvoice: true, canPickDate: false });

        try {
            const resp = await httpClient.js(
                ApiRoutes.typeAheads.getProjectInvoiceDate(timesheetId),
            );

            const lastInvoiced = resp?.value ?? null;

            if (!lastInvoiced) {
                this.setState({
                    lastInvoicedDate: null,
                    minSelectableDate: '',
                    canPickDate: true,
                    checkingInvoice: false,
                });
                return;
            }

            const d = new Date(lastInvoiced);
            const nextDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
            const minStr = formatDateYYYYMMDD(nextDay);

            const currentDateStr = this.state.data.selectedDate;
            let newSelectedDate = currentDateStr;
            if (currentDateStr) {
                const current = new Date(currentDateStr);
                if (current <= d) newSelectedDate = '';
            }

            this.setState((prev) => ({
                lastInvoicedDate: lastInvoiced,
                minSelectableDate: minStr,
                data: { ...prev.data, selectedDate: newSelectedDate },
                canPickDate: true,
                checkingInvoice: false,
            }));
        } catch (err) {
            console.error('Could not check last invoiced date:', err);
            toasty.error('Could not check last invoiced date. Please try again.');
            this.setState({
                lastInvoicedDate: null,
                minSelectableDate: '',
                canPickDate: false,
                checkingInvoice: false,
            });
        }
    }

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => {
                this.setState({ timesheetOptions: data });
                callback(data);
            });
    }, 250);

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { data } = this.state;

        if (!data.timesheetId) {
            toasty.error('Please select a timesheet.');
            return;
        }

        if (!data.selectedDate && !data.newStartTime) {
            toasty.error('Please provide at least a new date or a new start time.');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.changeJobDateAndTime(), {
                timesheetId: data.timesheetId,
                selectedDate: data.selectedDate ? new Date(data.selectedDate) : null,
                newStartTime: data.newStartTime || null,
            });

            toasty.success('Successfully updated job');

            this.setState({
                data: new ChangeJobDateViewModel(),
                lastInvoicedDate: null,
                minSelectableDate: '',
                canPickDate: false,
                timesheetOptions: [],
                changeStartTime: false,
            });

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error('Error updating job:', err);
            toasty.error('Error updating job. Please check the console.');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const {
            saving, data, timesheetOptions, minSelectableDate, changeStartTime, canPickDate,
        } = this.state;
        const isSubmitDisabled = saving || !data.timesheetId || (!data.selectedDate && !data.newStartTime);

        return (
            <form onSubmit={this.onSubmit}>
                <ValidatedSelect
                    component={AsyncSelect}
                    label="Timesheet Number"
                    loadOptions={this.loadTimesheetOptions}
                    placeholder="Type Timesheet Number Here"
                    name="data.timesheetId"
                    id="timesheetNumber"
                    value={(timesheetOptions ?? []).find((x) => x.value === data.timesheetId) ?? null}
                    onChange={(opt, meta) => {
                        this.onReactSelectChanged(opt, meta);
                        this.setState({ canPickDate: false });
                        this.checkLastInvoiced(opt?.value || '');
                    }}
                    cacheOptions
                    required
                    error="Timesheet Number is required"
                />

                <h6 className="mt-3 mb-2 font-weight-bold">Change Date</h6>

                <Input
                    label="New Date"
                    id="selectedDate"
                    name="data.selectedDate"
                    value={data.selectedDate ?? ''}
                    onChange={this.onChange}
                    type="date"
                    min={minSelectableDate || undefined}
                    required
                    disabled={!canPickDate}
                />

                <TipAlert
                    tipText="Note"
                    label={(
                        <>
                            <p className="mb-2">
                                Timesheets in the following statuses cannot be moved:
                            </p>
                            <ul className="list-disc pl-5 my-0">
                                <li>Approved</li>
                                <li>Exported</li>
                                <li>Deleted</li>
                            </ul>
                            <p className="mt-2">
                                When moving a rental timesheet, the Rental Start/End Date will not change.
                            </p>
                        </>
                    )}
                />

                <div className="mt-4 mb-2">
                    <FormCheckbox
                        id="changeStartTime"
                        name="changeStartTime"
                        checked={changeStartTime}
                        onChange={this.onChangeStartTimeToggle}
                        disabled={!data.timesheetId}
                        labelText="Change start time"
                        labelClass="font-weight-bold"
                    />
                </div>

                {changeStartTime && (
                    <Input
                        label="New Start Time"
                        id="newStartTime"
                        name="data.newStartTime"
                        value={data.newStartTime ?? ''}
                        onChange={this.onChange}
                        type="time"
                    />
                )}

                <BlockBorder />

                <div className="d-flex justify-content-center">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution history details for ChangeJobDate.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const ChangeJobDateHistoryDetails = ({ record }) => (
    <>
        <div>
            <strong>From:</strong>
            <List type="unstyled">
                {record.input.timesheetId && (
                    <li>
                        <strong>Timesheet ID:</strong>
                        {' '}
                        {record.input.timesheetId}
                    </li>
                )}
                {record.input.timesheetNumber && (
                    <li>
                        <strong>Timesheet:</strong>
                        {' '}
                        {record.input.timesheetNumber}
                    </li>
                )}
                {record.input.originalDate && (
                    <li>
                        <strong>Timesheet Date:</strong>
                        {' '}
                        {record.input.originalDate}
                    </li>
                )}
                {record.input.status && (
                    <li>
                        <strong>Timesheet Status:</strong>
                        {' '}
                        {record.input.status}
                    </li>
                )}
            </List>
        </div>

        <div>
            <strong>To:</strong>
            <List type="unstyled">
                {record.output.targetDate && (
                    <li>
                        <strong>Updated Date:</strong>
                        {' '}
                        {record.output.targetDate}
                    </li>
                )}
                {record.output.newStartTime && (
                    <li>
                        <strong>New Start Time:</strong>
                        {' '}
                        {record.output.newStartTime}
                    </li>
                )}
                {record.output.updatedEntity && (
                    <li>
                        <strong>Updated:</strong>
                        {' '}
                        {record.output.updatedEntity}
                    </li>
                )}
            </List>
        </div>
    </>
);

export { ChangeJobDateForm };
export default ChangeJobDateForm;
